import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AspectRatio, Language } from "@acs/shared";
import { api } from "../api";

const ALL_LANGS: Language[] = ["EN", "TR", "DE", "FR", "ES", "IT", "PT", "AR", "RU", "NL", "PL"];
const ALL_SIZES: AspectRatio[] = ["1:1", "9:16", "4:5", "16:9"];

type Operation = "translate" | "resize" | "video";

const OPERATIONS: { value: Operation; label: string; desc: string }[] = [
  { value: "translate", label: "Dil çoklama", desc: "Mevcut görseli seçilen dillere çevir" },
  { value: "resize",    label: "Boyut alma",  desc: "Mevcut görseli farklı boyutlara uyarla" },
  { value: "video",     label: "Videolaştırma", desc: "Görselden kısa video üret (veo3.1 fast)" },
];

export function EditKreatif({ productId }: { productId: string }) {
  const navigate = useNavigate();

  const [rawCodes, setRawCodes] = useState("");
  const [operation, setOperation] = useState<Operation>("translate");

  // Dil çoklama params
  const [languages, setLanguages] = useState<Language[]>([]);

  // Boyut alma params
  const [sizes, setSizes] = useState<AspectRatio[]>([]);

  // Videolaştırma params
  const [videoDuration, setVideoDuration] = useState<4 | 6 | 8>(8);
  const [videoSize, setVideoSize] = useState<AspectRatio>("9:16");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string[]>([]);

  // Parse codes from textarea
  const codes = rawCodes
    .split(/[\n,\s]+/)
    .map((c) => c.trim().replace(/^0+/, "").padStart(5, "0"))
    .filter((c) => /^\d{5}$/.test(c));

  const uniqueCodes = [...new Set(codes)];

  const canSubmit =
    uniqueCodes.length > 0 &&
    ((operation === "translate" && languages.length > 0) ||
      (operation === "resize" && sizes.length > 0) ||
      operation === "video");

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.createEditJob({
        product_id: productId,
        codes: uniqueCodes,
        operation,
        languages: operation === "translate" ? languages : undefined,
        sizes: operation === "resize" ? sizes : undefined,
        video_duration: operation === "video" ? videoDuration : undefined,
        video_size: operation === "video" ? videoSize : undefined,
      });
      navigate(`/products/${productId}/queue`);
    } catch (e) {
      setError(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="p-8 max-w-2xl">
      <h1 className="text-xl font-bold mb-1">Edit Kreatif</h1>
      <p className="text-sm text-zinc-500 mb-6">
        Mevcut kreatiflere yeni dil, boyut veya video ekle.
      </p>

      <div className="space-y-5 bg-white border rounded-lg p-6">

        {/* Kod input */}
        <Row label="Kodlar">
          <textarea
            value={rawCodes}
            onChange={(e) => setRawCodes(e.target.value)}
            rows={4}
            placeholder={"00001\n00002\n00003\n\nVirgülle ya da satır satır girilebilir"}
            className="w-full border rounded px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
          {uniqueCodes.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {uniqueCodes.map((c) => (
                <span key={c} className="inline-block px-2 py-0.5 bg-zinc-100 text-zinc-700 text-xs rounded font-mono">
                  {c}
                </span>
              ))}
              <span className="text-xs text-zinc-400 ml-1 self-center">{uniqueCodes.length} kod</span>
            </div>
          )}
        </Row>

        {/* İşlem seçimi */}
        <Row label="İşlem">
          <div className="flex flex-col gap-2">
            {OPERATIONS.map((op) => (
              <label
                key={op.value}
                className={`flex items-start gap-3 border rounded-lg px-4 py-3 cursor-pointer transition-colors ${
                  operation === op.value
                    ? "border-accent bg-accent/5"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
              >
                <input
                  type="radio"
                  name="operation"
                  value={op.value}
                  checked={operation === op.value}
                  onChange={() => setOperation(op.value)}
                  className="mt-0.5 accent-accent"
                />
                <div>
                  <div className="text-sm font-medium text-zinc-900">{op.label}</div>
                  <div className="text-xs text-zinc-500">{op.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </Row>

        {/* Dynamic options based on operation */}
        {operation === "translate" && (
          <Row label="Diller">
            <div className="flex flex-wrap gap-2">
              {ALL_LANGS.map((lang) => {
                const on = languages.includes(lang);
                return (
                  <button
                    key={lang}
                    type="button"
                    onClick={() =>
                      setLanguages(on ? languages.filter((l) => l !== lang) : [...languages, lang])
                    }
                    className={`px-3 py-1 text-sm rounded border transition-colors ${
                      on
                        ? "bg-accent text-white border-accent"
                        : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                    }`}
                  >
                    {lang}
                  </button>
                );
              })}
            </div>
            {languages.length === 0 && (
              <p className="text-xs text-zinc-400 mt-2">En az bir dil seç.</p>
            )}
          </Row>
        )}

        {operation === "resize" && (
          <Row label="Boyutlar">
            <div className="flex flex-wrap gap-2">
              {ALL_SIZES.map((size) => {
                const on = sizes.includes(size);
                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() =>
                      setSizes(on ? sizes.filter((s) => s !== size) : [...sizes, size])
                    }
                    className={`px-3 py-1 text-sm rounded border transition-colors ${
                      on
                        ? "bg-accent text-white border-accent"
                        : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                    }`}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
            {sizes.length === 0 && (
              <p className="text-xs text-zinc-400 mt-2">En az bir boyut seç.</p>
            )}
          </Row>
        )}

        {operation === "video" && (
          <>
            <Row label="Çıktı boyutu">
              <div className="flex flex-wrap gap-2">
                {ALL_SIZES.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setVideoSize(size)}
                    className={`px-3 py-1 text-sm rounded border transition-colors ${
                      videoSize === size
                        ? "bg-accent text-white border-accent"
                        : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              <p className="text-xs text-zinc-400 mt-1.5">
                Seçilen boyuttaki görsel input olarak kullanılır.
              </p>
            </Row>
            <Row label="Süre">
              <div className="flex gap-2">
                {([4, 6, 8] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setVideoDuration(d)}
                    className={`px-4 py-1.5 text-sm rounded border transition-colors ${
                      videoDuration === d
                        ? "bg-accent text-white border-accent"
                        : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                    }`}
                  >
                    {d}s
                  </button>
                ))}
              </div>
              <p className="text-xs text-zinc-400 mt-1.5">
                Animasyon promptu creative type'a göre otomatik seçilir (veo3.1 fast).
              </p>
            </Row>
          </>
        )}

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="border-t pt-4 flex items-center justify-between">
          <div className="text-sm text-zinc-500">
            {uniqueCodes.length > 0 ? (
              <>
                <span className="font-medium text-zinc-800">{uniqueCodes.length}</span> kreatif
                {operation === "translate" && languages.length > 0 && (
                  <> × <span className="font-medium text-zinc-800">{languages.length}</span> dil</>
                )}
                {operation === "resize" && sizes.length > 0 && (
                  <> × <span className="font-medium text-zinc-800">{sizes.length}</span> boyut</>
                )}
                {operation === "video" && (
                  <> → <span className="font-medium text-zinc-800">{videoDuration}s</span> video</>
                )}
              </>
            ) : (
              "Kod girin"
            )}
          </div>
          <button
            disabled={!canSubmit || submitting}
            onClick={submit}
            className="px-4 py-2 rounded bg-accent text-white text-sm font-medium disabled:opacity-50 hover:bg-accent/90 transition-colors"
          >
            {submitting ? "Gönderiliyor…" : "Kuyruğa Ekle"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-24 text-sm font-medium text-zinc-700 mt-2 shrink-0">{label}</div>
      <div className="flex-1">{children}</div>
    </div>
  );
}
